// covered-by: frontends/carrie/src/components/__tests__/Markdown.test.tsx

/**
 * The inline claim citation: the wording itself, as the link to what it rests on.
 *
 * Its own module, not another block appended to `Markdown.tsx`: that renderer is
 * the app-wide leaf every chat message and review comment mounts and was already
 * far past the 400-line expert-legibility bar before this existed. This half is
 * self-contained -- it reads one matched claim and decides which of three places
 * the wording opens (the registry row, the memory that recorded it, the
 * conversation that discussed it) -- so `Markdown.tsx` keeps only the
 * composition: the tag -> renderer entry and the plugin it comes from.
 *
 * Sibling of `Markdown.tsx` rather than a nested folder so the module paths this
 * file reaches for (`../ask-v2/paths`, `../routes/...`) are the ones it had, and
 * the move changes no resolution.
 */

import { Link as RouterLink } from "@tanstack/react-router";
import { type ReactNode } from "react";

import { ASK_V2_CHAT_PATH } from "../ask-v2/paths";
import rehypeClaimCitation, {
  type ClaimCitationOptions,
  type LinkableClaim,
} from "../plugins/rehype-claim-citation";
import { memorySearchTerm } from "../routes/settings/routes/knowledge-database/memorySearch";
// why the leaf `search.ts` and not `route.ts`: this module is mounted by the
// renderer that draws every chat message and review comment in the app, and
// `route.ts` calls `createRoute` at module scope and imports the whole Knowledge
// Database `view`. That top-level side effect cannot be tree-shaken, so
// importing from there would evaluate the KB page everywhere markdown renders.
// `search.ts` imports only zod + a parser.
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  KNOWLEDGE_DATABASE_PATH,
} from "../routes/settings/routes/knowledge-database/search";

/**
 * The citation's number, rendered beside the words it cites.
 *
 * This is the whole connection between the three places a citation appears: the
 * chips under a message are numbered `index + 1` over the same list, and the
 * rail lists the same records. Without it a reader sees twelve chips, three
 * underlined phrases, and no way to tell which is which.
 */
function CitationMarker({ index }: { index?: number }) {
  if (!index) return null;
  return (
    <sup className="puntt-citation-marker ml-0.5 text-[0.7em] font-semibold tabular-nums no-underline">
      {index}
    </sup>
  );
}

/**
 * The quoted claim, as the link to the rule it rests on.
 *
 * A router Link and not an `a`: the anchor renderer in `Markdown.tsx` forces
 * `target="_blank"`, which would full-page-reload out of the app and lose the
 * review the reader is standing in. This navigates in-app, to the Rules tab
 * with the cited source open at the row the claim came from.
 */
export default function ClaimCitationLink({
  ruleId,
  sourceId,
  cite,
  chatId,
  chatLabel,
  memoryId,
  memoryLabel,
  index,
  scope,
  occurrence,
  children,
}: {
  ruleId?: string;
  sourceId?: string;
  cite?: string;
  chatId?: string;
  chatLabel?: string;
  memoryId?: string;
  memoryLabel?: string;
  index?: number;
  scope?: string;
  occurrence?: number;
  children?: ReactNode;
}) {
  if (!ruleId) return <>{children}</>;

  // Scoped so the id resolves to exactly one node (testid-a11y): `scope` keeps
  // two comment bodies apart on a review page that renders many in one pass,
  // and `occurrence` keeps two mentions of the same rule apart inside one body.
  // Both are suffixes only when they are needed, so a single mention in a
  // standalone body stays the bare rule id.
  const testId = [
    ruleId,
    scope || null,
    occurrence && occurrence > 1 ? String(occurrence) : null,
  ]
    .filter(Boolean)
    .join("-");

  const chatTitle = chatLabel
    ? `Discussed in "${chatLabel}"`
    : "Discussed in an earlier conversation";
  const memoryTitle = memoryLabel
    ? `Recorded: ${memoryLabel}`
    : "Recorded in enterprise memory";

  // A claim can be BOTH recorded and discussed -- routinely is, because a
  // memory and the conversation it came up in land on the same claim. The
  // wording can only carry one link, so the other rides beside it as a small
  // trailing word. Rendered as a SIBLING, never nested: an anchor inside an
  // anchor is invalid HTML and browsers unnest it, which would drop it.
  const aside =
    memoryId && chatId ? (
      <RouterLink
        data-testid={`claim-citation-chat-aside-${testId}`}
        to={ASK_V2_CHAT_PATH}
        params={{ chatId }}
        title={chatTitle}
        aria-label={chatTitle}
        className="puntt-link-chat ml-0.5 align-super text-[0.7em] font-semibold no-underline"
      >
        chat
      </RouterLink>
    ) : null;

  // Recorded beats discussed for the WORDING itself: a memory is the durable
  // record of what was decided, where a chat is where it was said. The
  // conversation is still one click away, in its own colour.
  //
  // why the memory link SEARCHES: the graph names a memory by its `node_id`
  // ("mem_20260412_6f4515") while the memories DTO exposes the Mongo `_id`
  // (`enterpriseMemory.ts`). Different identifiers, so an id deep link
  // resolves to no row; the memory's own opening words are what the panel can
  // actually find.
  //
  // The `q` is consumed on the other end: `knowledge-database/view.tsx` hands
  // it to `MemoriesPanel initialQuery`, which seeds its search state and box
  // the way RulesPanel seeds from the same param. Omitted when the term is
  // empty, so the link never advertises a filter it cannot apply.
  if (memoryId) {
    const memoryQuery = memorySearchTerm(memoryLabel);
    return (
      <>
        <RouterLink
          data-testid={`claim-citation-memory-link-${testId}`}
          to={KNOWLEDGE_DATABASE_PATH}
          search={{
            ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
            tab: "memories",
            ...(memoryQuery ? { q: memoryQuery } : {}),
          }}
          title={memoryTitle}
          aria-label={memoryTitle}
          className="puntt-link-memory underline decoration-dotted underline-offset-2"
        >
          {children}
          <CitationMarker index={index} />
        </RouterLink>
        {aside}
      </>
    );
  }

  // Discussed but never recorded: the wording opens the conversation.
  if (chatId) {
    return (
      <RouterLink
        data-testid={`claim-citation-chat-link-${testId}`}
        to={ASK_V2_CHAT_PATH}
        params={{ chatId }}
        title={chatTitle}
        aria-label={chatTitle}
        className="puntt-link-chat underline decoration-dotted underline-offset-2"
      >
        {children}
        <CitationMarker index={index} />
      </RouterLink>
    );
  }

  return (
    <RouterLink
      data-testid={`claim-citation-link-${testId}`}
      to={KNOWLEDGE_DATABASE_PATH}
      search={{
        ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
        tab: "rules",
        ruleId,
        ...(sourceId ? { sourceId } : {}),
        ...(cite ? { cite } : {}),
      }}
      className="puntt-link underline decoration-dotted underline-offset-2"
    >
      {children}
      <CitationMarker index={index} />
    </RouterLink>
  );
}

/**
 * The rehype plugin entry that emits the tag this component renders.
 *
 * Here beside the renderer because the two are one contract: the plugin writes
 * `CLAIM_CITATION_TAG` nodes carrying exactly the props above, and nothing else
 * in the app produces or consumes them. `Markdown.tsx` calls this only when a
 * caller passed claims, so a body with none never constructs the plugin.
 */
export function claimCitationRehypePlugin(
  claims: LinkableClaim[],
  scope?: string,
): [typeof rehypeClaimCitation, ClaimCitationOptions] {
  return [rehypeClaimCitation, { claims, scope }];
}
